import { useEffect, useState } from 'react';
import { Pencil, Save, X, History } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import type { PromptDetail } from '../../lib/prompts-api';

export type EditorMode = 'view' | 'edit';

interface Props {
  detail: PromptDetail & { displayName: string };
  mode: EditorMode;
  draft?: string;
  onDraftChange?: (next: string) => void;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  onShowHistory: () => void;
  saving?: boolean;
  dirty?: boolean;  // parent-controlled; falls back to body-diff if undefined
}

export function PromptEditor({
  detail, mode, draft, onDraftChange, onEdit, onCancel, onSave, onShowHistory, saving, dirty,
}: Props) {
  const [localDraft, setLocalDraft] = useState(detail.body);

  useEffect(() => {
    setLocalDraft(detail.body);
  }, [detail.body, detail.setting]);

  const effectiveDraft = draft ?? localDraft;
  const isDirty = dirty !== undefined
    ? dirty
    : (mode === 'edit' && effectiveDraft !== detail.body);
  const charCount = effectiveDraft.length;

  return (
    <div className="flex flex-col h-full p-4 gap-3">
      <div className="flex items-baseline gap-2">
        <h2 className="text-lg font-bold">{detail.displayName}</h2>
        {isDirty && (
          <span className="text-xs text-amber-700 font-medium">● unsaved</span>
        )}
        <Badge
          variant="outline"
          className={cn(
            'ml-auto text-[10px]',
            mode === 'edit'
              ? 'bg-amber-50 border-amber-200 text-amber-800'
              : 'bg-gray-50 border-gray-200 text-gray-600',
          )}
        >
          {mode === 'edit' ? 'editing' : 'view'}
        </Badge>
      </div>
      <div className="text-xs text-muted-foreground">
        {detail.source === 'markdown'
          ? <>Source: markdown default · <code>docs/clinical-prompts/{detail.setting}.md</code></>
          : <>last edited {new Date(detail.version!.createdAt).toLocaleString()} by {detail.version!.createdBy} · v{detail.version!.id}</>
        }
        {' · '}{charCount} chars
      </div>

      {mode === 'view' ? (
        <pre className="flex-1 overflow-auto whitespace-pre-wrap font-mono text-xs bg-gray-50 border border-gray-200 rounded-md p-3 text-gray-700">
          {detail.body}
        </pre>
      ) : (
        <textarea
          value={effectiveDraft}
          onChange={(e) => {
            setLocalDraft(e.target.value);
            onDraftChange?.(e.target.value);
          }}
          className="flex-1 font-mono text-xs bg-amber-50 border border-amber-300 ring-2 ring-amber-200 rounded-md p-3 text-gray-900 outline-none resize-none"
        />
      )}

      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {mode === 'view' ? 'Read-only' : 'Editing — saving will affect new sessions immediately.'}
        </span>
        <div className="flex gap-2">
          {mode === 'view' ? (
            <>
              <Button variant="outline" size="sm" onClick={onShowHistory}>
                <History className="h-3.5 w-3.5 mr-1" /> View history
              </Button>
              <Button onClick={onEdit} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
                <X className="h-3.5 w-3.5 mr-1" /> Cancel
              </Button>
              <Button
                onClick={onSave}
                size="sm"
                disabled={saving || !isDirty}
                className="bg-amber-500 hover:bg-amber-600 text-white"
              >
                <Save className="h-3.5 w-3.5 mr-1" /> {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
